import { cn } from '~/lib/utils'
import sanitizeHtml from 'sanitize-html'

import MarkDown from '~/components/Helpers/MarkDown'
import Icon, { IconProps } from '~/components/Helpers/Icon'
import Tag, { TagProps } from '~/components/UI/General/Tag/Tag'
import CTABlock from '~/components/UI/Buttons/helpers/CTABlock'

export type StandardTextProps = {
  content: {
    component?: StandardTextProps['content']
    alignment?: 'left' | 'centre' | 'right'
    icon?: IconProps['icon']
    eyebrow?: string
    heading?: string
    tags?: Array<TagProps['data']>
    copy?: string
    buttons?: Array<Button['data']>
    ctaButtons?: boolean
  }
  backgroundColour?: AllBackgroundColours
  layoutColour?: AllBackgroundColours
  className?: string
}

const StandardText = ({
  content,
  backgroundColour,
  layoutColour,
  className
}: StandardTextProps) => {
  const { heading, eyebrow, tags, copy, alignment, icon, buttons, ctaButtons } =
    (content?.component || content) ?? {}

  const iconData = Array.isArray(icon) ? icon[0] : icon

  return (
    <div
      className={cn(
        className,
        alignment === 'centre' ? 'text-center' : '',
        alignment === 'right' ? 'text-right' : '',
        ['ink-blue', 'mid-blue'].includes(
          backgroundColour || layoutColour || ''
        )
          ? 'text-white'
          : ['white', 'light-grey', 'chartreuse'].includes(
                backgroundColour || layoutColour || ''
              )
            ? 'text-blue-d [&_a]:text-blue-m [&_a]:hover:text-white'
            : 'text-blue-d [&_a]:text-blue-d [&_a]:hover:text-white',
        'space-y-s'
      )}
    >
      {iconData && (
        <Icon
          className={cn(
            'size-m sm:size-l flex w-full',
            alignment === 'centre' ? 'justify-center' : '',
            alignment === 'right' ? 'justify-end' : ''
          )}
          icon={iconData}
          backgroundColour={backgroundColour}
        />
      )}
      {eyebrow && <div className='tag mb-xs'>{eyebrow}</div>}
      {heading && <MarkDown content={'## ' + heading} />}
      {tags && tags.length > 0 && (
        <div
          className={cn(
            ['ink-blue', 'mid-blue'].includes(
              backgroundColour || layoutColour || ''
            )
              ? 'border-white/60'
              : 'border-blue-d/60 [&_a]:!text-blue-d',
            'pb-xs border-b'
          )}
        >
          <ul
            className={cn(
              'space-x-xs-plus gap-y-xs flex list-none flex-wrap items-center pl-0',
              alignment === 'centre' ? 'justify-center' : '',
              alignment === 'right' ? 'justify-end' : ''
            )}
          >
            {tags.map((tag, index: Index) => (
              <li key={index}>
                <Tag data={tag} layoutColour={backgroundColour} />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className='space-y-xs grow'
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(copy || '', {
            allowedTags: [
              'em',
              'strong',
              'h2',
              'h3',
              'h4',
              'a',
              'p',
              'br',
              'ul',
              'ol',
              'li',
              'sup'
            ]
          })
        }}
      ></div>
      {buttons && buttons.length > 0 && (
        <CTABlock
          content={buttons}
          ctaButtons={ctaButtons}
          backgroundColour={backgroundColour || layoutColour}
          className={cn(
            'mt-m lg:mt-[40px]',
            alignment === 'centre' ? 'justify-center' : '',
            alignment === 'right' ? 'justify-end' : ''
          )}
        />
      )}
    </div>
  )
}

export default StandardText
